/**
 * Go Game — Main Container Component
 *
 * Orchestrates: mode selection → engine init → stone placement →
 * capture handling → pass/resign → game over scoring.
 *
 * Uses UniversalGameLayout for consistent layout across all games.
 * Wrapped in ErrorBoundary. Uses useAuthentication().
 */

import React, { useState, useCallback, useEffect, useRef } from 'react';
import { ErrorBoundary } from '../../components/ErrorBoundary';
import { UniversalGameLayout, UniversalGameLayoutConfig } from '../../components/UniversalGameLayout';
import { useAuthentication } from '../../hooks/useAuthentication';
import { useNotifications } from '../../hooks/useNotifications';
import { NotificationType } from '../../services/NotificationService';
import { GameSessionManager } from '../../services/GameSessionManager';
import { GameCoachService } from '../../services/GameCoachService';
import { goKnowledge } from '../../services/gameCoachKnowledge';
import { GameCoach } from '../../components/GameCoach';
import { GoEngine } from '../GoEngine';
import { GoAI } from '../GoAI';
import { GoBoard } from './GoBoard';
import UniversalGameModeSelector, { GameModeConfig, GameModeSelection } from '../../components/UniversalGameModeSelector';
import { GoErrorHandler } from '../services/GoErrorHandler';
import { GoStatisticsService } from '../services/GoStatisticsService';
import { GoGamePersistence } from '../services/GoGamePersistence';
import { ErrorHandlingService } from '../../services/ErrorHandlingService';
import { NotificationService } from '../../services/NotificationService';
import { StatisticsManager } from '../../services/StatisticsManager';
import { ProfileManager } from '../../services/ProfileManager';
import { LocalStorageManager } from '../../services/LocalStorageManager';
import {
  GoGameSettings,
  GoGameState,
  GoColor,
  GoPosition,
  GoGameProps,
} from '../types';
import './GoGame.css';

type GameView = 'mode-select' | 'playing' | 'game-over';

const GoGameInner: React.FC<GoGameProps> = ({
  onExit,
  initialRoomCode,
}) => {
  const { isAuthenticated, user } = useAuthentication();
  const { addNotification } = useNotifications();

  // Game state
  const [view, setView] = useState<GameView>('mode-select');
  const [settings, setSettings] = useState<GoGameSettings | null>(null);
  const [gameState, setGameState] = useState<GoGameState | null>(null);
  const [lastMovePos, setLastMovePos] = useState<GoPosition | null>(null);

  // Refs
  const engineRef = useRef<GoEngine>(new GoEngine());
  const aiRef = useRef<GoAI | null>(null);
  const gameSessionIdRef = useRef<string | null>(null);
  const gameSessionManager = useRef(GameSessionManager.getInstance());
  const humanColorRef = useRef<GoColor>('black');
  const coachRef = useRef(new GameCoachService('Go', goKnowledge));

  // Services
  const errorHandlerRef = useRef<GoErrorHandler | null>(null);
  const statsServiceRef = useRef<GoStatisticsService | null>(null);
  const persistenceRef = useRef<GoGamePersistence | null>(null);

  // Initialize services once
  useEffect(() => {
    try {
      const errorService = ErrorHandlingService.getInstance();
      const notifService = NotificationService.getInstance();
      errorHandlerRef.current = new GoErrorHandler(errorService, notifService);

      const profileManager = new ProfileManager();
      const statsManager = new StatisticsManager(profileManager);
      statsServiceRef.current = new GoStatisticsService(statsManager);

      const storageManager = new LocalStorageManager();
      persistenceRef.current = new GoGamePersistence(storageManager);
    } catch (e) {
      console.error('Failed to initialize services:', e);
    }

    return () => {
      aiRef.current?.destroy();
    };
  }, []);

  // Check for saved game on mount
  useEffect(() => {
    if (persistenceRef.current?.hasAutoSave()) {
      addNotification({
        type: NotificationType.INFO,
        title: 'Saved Game Found',
        message: 'A saved game was found. Start a new game to continue.',
        duration: 4000,
      });
    }
  }, [addNotification]);

  // =========================================================================
  // Game Flow
  // =========================================================================

  const handleStartGame = useCallback(
    (newSettings: GoGameSettings) => {
      const engine = engineRef.current;
      engine.initializeGame(newSettings);

      const humanColor = newSettings.playerColor || 'black';
      humanColorRef.current = humanColor;

      setSettings(newSettings);
      setGameState(engine.getGameState());
      setLastMovePos(null);
      setView('playing');

      gameSessionIdRef.current = gameSessionManager.current.startGameSession('go');

      if (newSettings.mode === 'singleplayer' && newSettings.aiDifficulty) {
        aiRef.current = new GoAI(newSettings.aiDifficulty);
      } else {
        aiRef.current?.destroy();
        aiRef.current = null;
      }

      addNotification({
        type: NotificationType.SUCCESS,
        title: 'Game Started',
        message: `${newSettings.boardSize}×${newSettings.boardSize} Go game started!`,
        duration: 2000,
      });

      // If AI goes first (human chose white)
      if (
        newSettings.mode === 'singleplayer' &&
        humanColor === 'white' &&
        aiRef.current
      ) {
        const state = engine.getGameState();
        aiRef.current.scheduleMove(
          state.board,
          state.currentPlayer,
          state.boardSize,
          state.koPoint,
          (pos) => {
            if (pos) handleAIMove(pos);
            else handleAIPass();
          }
        );
      }
    },
    [addNotification]
  );

  const handleIntersectionClick = useCallback(
    (position: GoPosition) => {
      const engine = engineRef.current;
      if (engine.isGameOver()) return;

      const currentPlayer = engine.getCurrentPlayer();

      if (settings?.mode === 'singleplayer') {
        if (currentPlayer !== humanColorRef.current) return;
      }

      executeMove(position);
    },
    [settings]
  );

  const executeMove = useCallback(
    (position: GoPosition) => {
      const engine = engineRef.current;

      try {
        const captured = engine.placeStone(position.row, position.col);
        setLastMovePos(position);

        if (captured.length > 0) {
          addNotification({
            type: NotificationType.INFO,
            title: 'Capture!',
            message: `${captured.length} stone${captured.length > 1 ? 's' : ''} captured!`,
            duration: 1500,
          });
        }

        const newState = engine.getGameState();
        setGameState(newState);

        if (engine.isGameOver()) {
          handleGameOver(newState);
          return;
        }

        // Auto-save
        if (persistenceRef.current && settings) {
          persistenceRef.current.autoSave(settings.mode, newState, {
            aiDifficulty: settings.aiDifficulty,
            startedAt: newState.startedAt,
            lastSavedAt: Date.now(),
          });
        }

        // Schedule AI move
        if (
          settings?.mode === 'singleplayer' &&
          aiRef.current &&
          newState.currentPlayer !== humanColorRef.current &&
          !engine.isGameOver()
        ) {
          aiRef.current.scheduleMove(
            newState.board,
            newState.currentPlayer,
            newState.boardSize,
            newState.koPoint,
            (pos) => {
              if (pos) handleAIMove(pos);
              else handleAIPass();
            }
          );
        }
      } catch (error) {
        errorHandlerRef.current?.handleGameLogicError(
          error instanceof Error ? error.message : 'Move execution failed'
        );
      }
    },
    [addNotification, settings]
  );

  const handleAIMove = useCallback(
    (position: GoPosition) => {
      const engine = engineRef.current;
      if (engine.isGameOver()) return;
      executeMove(position);
    },
    [executeMove]
  );

  const handleAIPass = useCallback(() => {
    const engine = engineRef.current;
    if (engine.isGameOver()) return;

    engine.pass();
    const newState = engine.getGameState();
    setGameState(newState);

    addNotification({
      type: NotificationType.INFO,
      title: 'AI Passed',
      message: 'The AI has passed their turn.',
      duration: 2000,
    });

    if (engine.isGameOver()) {
      handleGameOver(newState);
    }
  }, [addNotification]);

  const handlePass = useCallback(() => {
    const engine = engineRef.current;
    if (engine.isGameOver()) return;

    const currentPlayer = engine.getCurrentPlayer();

    if (settings?.mode === 'singleplayer' && currentPlayer !== humanColorRef.current) {
      return;
    }

    engine.pass();
    setLastMovePos(null);
    const newState = engine.getGameState();
    setGameState(newState);

    addNotification({
      type: NotificationType.INFO,
      title: 'Pass',
      message: `${currentPlayer === 'black' ? 'Black' : 'White'} passed.`,
      duration: 2000,
    });

    if (engine.isGameOver()) {
      handleGameOver(newState);
      return;
    }

    // Schedule AI move after human pass
    if (
      settings?.mode === 'singleplayer' &&
      aiRef.current &&
      newState.currentPlayer !== humanColorRef.current
    ) {
      aiRef.current.scheduleMove(
        newState.board,
        newState.currentPlayer,
        newState.boardSize,
        newState.koPoint,
        (pos) => {
          if (pos) handleAIMove(pos);
          else handleAIPass();
        }
      );
    }
  }, [addNotification, settings, handleAIMove, handleAIPass]);

  const handleResign = useCallback(() => {
    const engine = engineRef.current;
    if (engine.isGameOver()) return;

    // Force game over — the resigning player loses
    const state = engine.getGameState();
    const score = engine.calculateScore();
    const resigningPlayer = state.currentPlayer;
    const winner = resigningPlayer === 'black' ? 'white' : 'black';

    const finalState: GoGameState = {
      ...state,
      gameStatus: 'completed',
      winner,
      score,
      territory: engine.calculateTerritory(),
    };

    engine.setGameState(finalState);
    setGameState(finalState);
    handleGameOver(finalState);

    addNotification({
      type: NotificationType.WARNING,
      title: 'Resigned',
      message: `${resigningPlayer === 'black' ? 'Black' : 'White'} resigned. ${winner === 'black' ? 'Black' : 'White'} wins!`,
      duration: 4000,
    });
  }, [addNotification]);

  const handleGameOver = useCallback(
    (state: GoGameState) => {
      setGameState(state);
      setView('game-over');

      if (gameSessionIdRef.current) {
        gameSessionManager.current.endGameSession(gameSessionIdRef.current, true);
        gameSessionIdRef.current = null;
      }

      persistenceRef.current?.clearAutoSave();

      if (statsServiceRef.current && settings && state.score) {
        statsServiceRef.current.recordGameResult(
          {
            gameId: state.gameId,
            mode: settings.mode,
            difficulty: settings.aiDifficulty,
            winner: state.winner,
            score: state.score,
            totalMoves: state.moveHistory.length,
            totalCaptures: state.captures.black + state.captures.white,
            duration: Math.floor((Date.now() - state.startedAt) / 1000),
            completedAt: Date.now(),
            boardSize: state.boardSize,
          },
          humanColorRef.current,
          isAuthenticated ? user?.id : undefined
        );
      }

      const winner = state.winner;
      if (winner === 'draw') {
        addNotification({
          type: NotificationType.INFO,
          title: '🤝 Draw!',
          message: `Game ended in a draw! ${state.score?.black.toFixed(1)} - ${state.score?.white.toFixed(1)}`,
          duration: 5000,
        });
      } else if (winner) {
        addNotification({
          type: NotificationType.SUCCESS,
          title: '🎉 Game Over!',
          message: `${winner === 'black' ? '⚫ Black' : '⚪ White'} wins ${state.score?.black.toFixed(1)} - ${state.score?.white.toFixed(1)}!`,
          duration: 5000,
        });
      }
    },
    [addNotification, settings, isAuthenticated, user]
  );

  const handleNewGame = useCallback(() => {
    aiRef.current?.destroy();
    aiRef.current = null;
    setView('mode-select');
    setGameState(null);
    setLastMovePos(null);
    setSettings(null);
  }, []);

  const handleExit = useCallback(() => {
    if (gameSessionIdRef.current) {
      gameSessionManager.current.endGameSession(gameSessionIdRef.current, false);
      gameSessionIdRef.current = null;
    }
    aiRef.current?.destroy();
    onExit?.();
  }, [onExit]);

  // Trigger AI on initial turn if AI goes first
  useEffect(() => {
    if (view !== 'playing' || !gameState || !aiRef.current) return;
    const engine = engineRef.current;
    const currentPlayer = engine.getCurrentPlayer();
    if (
      currentPlayer !== humanColorRef.current &&
      !engine.isGameOver()
    ) {
      aiRef.current.scheduleMove(
        gameState.board,
        currentPlayer,
        gameState.boardSize,
        gameState.koPoint,
        (pos) => {
          if (pos) handleAIMove(pos);
          else handleAIPass();
        }
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view]);

  // =========================================================================
  // Render
  // =========================================================================

  if (view === 'mode-select') {
    const goConfig: GameModeConfig = {
      gameName: 'Go',
      gameIcon: '⚫',
      subtitle: 'Ancient strategy game',
      availableModes: ['ai', 'local', 'online'],
    };

    const handleModeSelection = (selection: GameModeSelection) => {
      if (selection.mode === 'ai') {
        handleStartGame({
          mode: 'singleplayer',
          aiDifficulty: (selection.difficulty as any) || 'medium',
          boardSize: 9,
        });
      } else if (selection.mode === 'local') {
        handleStartGame({
          mode: 'local-multiplayer',
          boardSize: 9,
        });
      } else if (selection.mode === 'online') {
        handleStartGame({ mode: 'multiplayer-realtime', boardSize: 9 });
      }
    };

    return (
      <div className="go-game-container">
        <UniversalGameModeSelector
          config={goConfig}
          onStart={handleModeSelection}
          isAuthenticated={isAuthenticated}
          guestGamesRemaining={isAuthenticated ? undefined : 3}
        />
      </div>
    );
  }

  if (!gameState) return null;

  const engine = engineRef.current;
  const currentPlayer = gameState.currentPlayer;
  const isHumanTurn =
    settings?.mode !== 'singleplayer' || currentPlayer === humanColorRef.current;
  const isBoardDisabled = !isHumanTurn || gameState.gameStatus === 'completed';
  const isGameOver = gameState.gameStatus === 'completed';
  const validMoves = isHumanTurn && gameState.gameStatus === 'active'
    ? engine.getValidMoves()
    : [];

  // -------------------------------------------------------------------------
  // Game-over state
  // -------------------------------------------------------------------------

  const gameOverResult = (() => {
    if (!isGameOver) return 'draw' as const;
    if (settings?.mode === 'singleplayer') {
      return gameState.winner === humanColorRef.current ? 'win' as const : 'loss' as const;
    }
    if (gameState.winner === 'draw') return 'draw' as const;
    return 'win' as const;
  })();

  const gameOverTitle = (() => {
    if (gameState.winner === 'draw') return '🤝 Draw!';
    if (settings?.mode === 'singleplayer') {
      return gameState.winner === humanColorRef.current ? '🎉 Victory!' : '😞 Defeat!';
    }
    return '🎉 Game Over!';
  })();

  const gameOverSubtitle = (() => {
    if (!gameState.score) return '';
    if (gameState.winner === 'draw') {
      return `${gameState.score.black.toFixed(1)} - ${gameState.score.white.toFixed(1)}`;
    }
    const winnerLabel = gameState.winner === 'black' ? 'Black' : 'White';
    return `${winnerLabel} wins ${gameState.score.black.toFixed(1)} - ${gameState.score.white.toFixed(1)}`;
  })();

  const gameOverIcon = (() => {
    if (gameOverResult === 'win') return '🏆';
    if (gameOverResult === 'loss') return '😞';
    return '🤝';
  })();

  // -------------------------------------------------------------------------
  // Build UniversalGameLayout config
  // -------------------------------------------------------------------------

  const layoutConfig: UniversalGameLayoutConfig = (() => {
    // Left panels
    const leftPanels = [
      {
        id: 'captures',
        title: 'Captures',
        icon: '⚫',
        content: (
          <div className="go-game__captures-panel">
            <div className="go-game__capture-row">
              <span className="go-game__score-stone go-game__score-stone--black" aria-hidden="true" />
              <span>Black captured: <strong>{gameState.captures.black}</strong></span>
            </div>
            <div className="go-game__capture-row">
              <span className="go-game__score-stone go-game__score-stone--white" aria-hidden="true" />
              <span>White captured: <strong>{gameState.captures.white}</strong></span>
            </div>
          </div>
        ),
      },
      {
        id: 'score',
        title: 'Score & Territory',
        icon: '📊',
        content: (
          <div className="go-game__score-panel">
            {gameState.score ? (
              <>
                <div className="go-game__score-row">
                  <span>⚫ Black: <strong>{gameState.score.black.toFixed(1)}</strong></span>
                  <span className="go-game__score-detail">Territory: {gameState.score.blackTerritory}</span>
                </div>
                <div className="go-game__score-row">
                  <span>⚪ White: <strong>{gameState.score.white.toFixed(1)}</strong></span>
                  <span className="go-game__score-detail">Territory: {gameState.score.whiteTerritory}</span>
                </div>
              </>
            ) : (
              <div className="go-game__score-row">
                <span>Komi: {gameState.komi}</span>
                <span>Board: {gameState.boardSize}×{gameState.boardSize}</span>
              </div>
            )}
          </div>
        ),
      },
      {
        id: 'history',
        title: 'Move History',
        icon: '📜',
        content: (
          <div className="go-game__history-panel">
            <div className="go-game__history-count">
              Total moves: {gameState.moveHistory.length}
            </div>
            <div className="go-game__history-list">
              {gameState.moveHistory.slice(-20).map((move, idx) => (
                <div key={idx} className="go-game__history-entry">
                  <span className="go-game__history-num">{gameState.moveHistory.length - 20 + idx + 1}.</span>
                  <span>{move.player === 'black' ? '⚫' : '⚪'}</span>
                  <span>{move.move.type === 'pass' ? 'Pass' : `(${move.move.row}, ${move.move.col})`}</span>
                </div>
              ))}
            </div>
          </div>
        ),
        defaultFolded: true,
      },
    ];

    // Right panels (AI Coach in single-player mode)
    const rightPanels = settings?.mode === 'singleplayer' ? [
      {
        id: 'coach',
        title: 'AI Coach',
        icon: '🤖',
        content: (
          <GameCoach
            coach={coachRef.current}
            gameContext={{
              gameName: 'Go',
              gameState: `${gameState.boardSize}x${gameState.boardSize} board. Black captured: ${gameState.captures.black}, White captured: ${gameState.captures.white}. Move ${gameState.moveHistory.length}.`,
              playerContext: `Playing as ${humanColorRef.current}. ${isHumanTurn ? "It's your turn." : "Waiting for AI."}`,
            }}
            isPlayerTurn={isHumanTurn}
            quickQuestions={['Territory advice?', 'Good moves?', 'Life and death?', 'Opening tips?']}
            accentColor="#1a1a2e"
          />
        ),
      },
    ] : [];

    // Status bar
    const statusBar = {
      turnIndicator: (
        <span>
          {currentPlayer === 'black' ? '⚫' : '⚪'}{' '}
          {isGameOver
            ? 'Game Over'
            : !isHumanTurn
              ? 'AI thinking...'
              : `${currentPlayer === 'black' ? 'Black' : 'White'}'s turn`}
        </span>
      ),
      gameStatus: isGameOver ? '👑 Game Over' : undefined,
      gameStatusType: isGameOver ? 'ended' as const : 'active' as const,
      aiThinking: !isHumanTurn && !isGameOver,
    };

    // Actions
    const actions = [
      ...(gameState.gameStatus === 'active' && isHumanTurn ? [{
        id: 'pass',
        label: 'Pass',
        icon: '⏭️',
        onClick: handlePass,
        variant: 'secondary' as const,
      }] : []),
      ...(gameState.gameStatus === 'active' && isHumanTurn ? [{
        id: 'resign',
        label: 'Resign',
        icon: '🏳️',
        onClick: handleResign,
        variant: 'danger' as const,
      }] : []),
      {
        id: 'new-game',
        label: 'New Game',
        icon: '🔄',
        onClick: handleNewGame,
        variant: 'primary' as const,
      },
      ...(onExit ? [{
        id: 'exit',
        label: 'Exit',
        icon: '🚪',
        onClick: handleExit,
        variant: 'secondary' as const,
      }] : []),
    ];

    // Game over config
    const gameOver = isGameOver && gameState.score ? {
      show: true,
      result: gameOverResult,
      title: gameOverTitle,
      subtitle: gameOverSubtitle,
      icon: gameOverIcon,
      onPlayAgain: handleNewGame,
      onExit: onExit || handleNewGame,
    } : undefined;

    return {
      gameName: 'Go',
      board: (
        <GoBoard
          board={gameState.board}
          boardSize={gameState.boardSize}
          validMoves={validMoves}
          lastMove={lastMovePos}
          currentPlayer={currentPlayer}
          koPoint={gameState.koPoint}
          territory={gameState.territory}
          gameOver={isGameOver}
          onIntersectionClick={handleIntersectionClick}
          disabled={isBoardDisabled}
        />
      ),
      leftPanels,
      rightPanels,
      statusBar,
      actions,
      gameOver,
    };
  })();

  return (
    <div className="go-game-container">
      <UniversalGameLayout config={layoutConfig} />
    </div>
  );
};

/** Wrapped in ErrorBoundary */
const GoGame: React.FC<GoGameProps> = (props) => (
  <ErrorBoundary componentName="Go">
    <GoGameInner {...props} />
  </ErrorBoundary>
);

export { GoGame };
export default GoGame;
